import { isFeatureEnabled } from "@shared-core/config/features";
import { useState, useEffect } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  PromoOfficeBetterment,
  PromoOfficeIndent,
  PromoOfficeLineItem,
  PromoOfficeSyncStatus,
  PromoOfficeTender,
  PromoOfficeTenderPendingAction,
} from "@shared-core/data/adapter";
import { mutationErrorMessage } from "@shared-core/domain/mutation-error-message";
import { paths } from "@shared-core/routing/routes";
import {
  formatCurrencyGbp,
  formatShortDateOrDash,
} from "@shared-core/domain/formatters";
import {
  hasPublishedLadder,
  quotePriceColumns,
} from "@shared-core/domain/promo-office-pricing";
import type { QuotePriceColumn } from "@shared-core/domain/promo-office-pricing";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../app/components/primitives/table";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  TabsTriggerCount,
} from "../app/components/primitives/tabs";
import { ListPagination } from "../app/components/patterns/ListPagination";
import {
  CisCustomerCell,
  UnlinkedCustomersTab,
} from "../app/components/domain/promo-office/CustomerLinking";
import {
  LinkedDraftEstimateMarker,
  UnlinkedQuotesTab,
} from "../app/components/domain/promo-office/UnlinkedQuotes";
import { UnlinkedSuppliersTab } from "../app/components/domain/promo-office/SupplierLinking";
import { Card } from "../app/components/primitives/card";
import { Button } from "../app/components/primitives/button";
import { cn } from "../app/components/primitives/utils";
import { Textarea } from "../app/components/primitives/textarea";
import { Label } from "../app/components/primitives/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../app/components/primitives/dialog";
import {
  Gift,
  Loader2,
  Clock,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  XCircle,
  Printer,
} from "lucide-react";
import { currentTime } from "@shared-core/domain/current-time";

// Read-only view of Promo Office's own currently-open Tenders (quotes not
// yet converted to an Indent) and Indents (customer orders in process, not
// yet invoiced) — for staff without Promo Office access. Data is a
// periodically-refreshed cache, not a live call per page view, hence the
// staleness indicator. See docs/features/promo-office-visibility/01_product_brief.md.
//
// "Indent" is Promo Office's own term; the UI displays it as "Orders" to
// avoid colliding with this app's unrelated Tharstern Jobs page.
//
// Accept/decline on a Quote is NOT a write to Promo Office — CIS has no
// write path there yet. It notifies the merchandise team, who action the
// change in Promo Office itself; the Tender then drops off this list (or
// an Order appears) once the next periodic sync picks it up. The UI's job
// is just to make that "we've asked, nothing's changed yet" state visible.

function formatTimeAgo(iso: string | null): string {
  if (!iso) return "never";
  const diffMs = currentTime().getTime() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// Promo Office uses year 0001 as its "no date" sentinel, which the backend maps
// to null. Formatting that unguarded gives `new Date(null)` -- the Unix epoch --
// so a missing date renders as "1 Jan" instead of as missing.
//
// The formatting itself is shared-core's, not this page's: `londonParts` pins
// the timezone, where the local `toLocaleDateString` this replaced rendered in
// whatever timezone the viewer's browser is in. A page with its own date
// formatter is also a page that repo-wide date work does not reach -- #1935
// standardised UK date order across all three packages and could not see this
// one. The em-dash is kept rather than shared-core's "-" because every other
// absent value on this page reads as "—", and one column disagreeing with its
// neighbours is a worse trade than one page disagreeing with the app.
//
// That applies to an unparseable value too, not just null: shared-core returns
// its own "-" for both, so the swap back has to cover whatever it treats as
// absent rather than only the null the sentinel arrives as. Validity stays
// shared-core's call -- this only restyles the glyph it chose.
export function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const formatted = formatShortDateOrDash(iso);
  return formatted === "-" ? "—" : formatted;
}

function StalenessIndicator({
  status,
}: {
  status: PromoOfficeSyncStatus | null;
}) {
  if (!status) return null;

  if (status.refreshStatus === "never_run") {
    return (
      <span className="text-sm text-muted-foreground flex items-center gap-1.5">
        <Clock className="size-3.5" /> Not yet refreshed
      </span>
    );
  }

  // Every "data from" / "updated" time is `lastSucceededAt`: a failing lane is
  // re-attempted every tick, so the attempt time stays recent however old the
  // data is. Quoting it read "showing data from 2m ago" on staging over a
  // cache two days old.
  if (status.refreshStatus === "failed") {
    return (
      <span className="text-sm text-amber-600 flex items-center gap-1.5">
        <AlertTriangle className="size-3.5" />
        Last refresh failed {formatTimeAgo(status.lastRefreshedAt)} —{" "}
        {status.lastSucceededAt
          ? `showing data from ${formatTimeAgo(status.lastSucceededAt)}`
          : "no refresh has succeeded yet"}
      </span>
    );
  }

  if (status.refreshStatus === "partial") {
    return (
      <span className="text-sm text-amber-600 flex items-center gap-1.5">
        <AlertTriangle className="size-3.5" />
        Updated {formatTimeAgo(status.lastSucceededAt)} — some records could not
        be fetched and are missing
      </span>
    );
  }

  return (
    <span className="text-sm text-muted-foreground flex items-center gap-1.5">
      <Clock className="size-3.5" /> Last updated{" "}
      {formatTimeAgo(status.lastSucceededAt)}
    </span>
  );
}

function LineItemThumbnail({
  imageUrl,
  description,
}: {
  imageUrl: string | null;
  description: string;
}) {
  // A URL that fails to load has to degrade to the same placeholder as no URL
  // at all. These are the vendor's own image URLs, hotlinked straight from the
  // browser, so a dead or unreachable one is an ordinary state rather than an
  // exception -- and a broken-image glyph beside every product reads as the
  // page being broken. `no-referrer` because the vendor needs nothing from us
  // to serve the image, so it is told nothing, not even which site asked.
  // The host is allowed by name in the app's CSP (infra/Caddyfile).
  const [failed, setFailed] = useState(false);

  if (!imageUrl || failed) {
    return (
      <div
        className="size-20 rounded-md border border-border bg-muted flex items-center justify-center shrink-0"
        aria-label="No image available"
      >
        <Gift className="size-6 text-muted-foreground" />
      </div>
    );
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          className="size-20 rounded-md border border-border shrink-0 overflow-hidden cursor-zoom-in transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          aria-label={`View larger image of ${description}`}
        >
          <img
            src={imageUrl}
            alt={description}
            className="size-full object-cover"
            referrerPolicy="no-referrer"
            onError={() => setFailed(true)}
          />
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{description}</DialogTitle>
        </DialogHeader>
        <img
          src={imageUrl}
          alt={description}
          className="w-full max-h-[70vh] object-contain rounded-md"
          referrerPolicy="no-referrer"
        />
      </DialogContent>
    </Dialog>
  );
}

// Orders (Indents) keep the simple flat table — no margin/price-break
// rework there yet, matching how accept/decline are Quotes-only.
function OrderLineItemsTable({ lines }: { lines: PromoOfficeLineItem[] }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-20" />
          <TableHead>Description</TableHead>
          <TableHead>Supplier</TableHead>
          <TableHead className="text-right">Qty</TableHead>
          <TableHead className="text-right">Value</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {lines.map((line) => (
          <TableRow key={line.id}>
            <TableCell>
              {line.lineKind === "product" && (
                <LineItemThumbnail
                  imageUrl={line.imageUrl}
                  description={line.description}
                />
              )}
            </TableCell>
            <TableCell className="text-sm text-muted-foreground">
              <div>{line.description}</div>
              {line.betterments.map((b) => (
                <div key={b.id} className="text-xs mt-0.5">
                  + {b.name} ({b.supplierName})
                </div>
              ))}
            </TableCell>
            <TableCell className="text-sm text-muted-foreground">
              {line.supplierName ?? "—"}
            </TableCell>
            <TableCell className="text-right text-sm">
              {line.quantity}
            </TableCell>
            <TableCell className="text-right text-sm">
              {formatCurrencyGbp(line.sellValue, 2)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

// Decoration/customisation lines (Promo Office's own term: "Betterments")
// attached to a product line — engraving, print, embroidery, etc. Each has
// its own decorator (supplier) and a flat sell/cost figure, not a
// quantity-tiered price-break ladder like the product line itself, since
// that's how Promo Office models them. Quotes-only, same as the rest of
// this per-product card — Orders keep the plain flat table.
function LineBetterments({
  betterments,
}: {
  betterments: PromoOfficeBetterment[];
}) {
  if (betterments.length === 0) return null;

  return (
    <div className="pt-1 space-y-1.5 border-t border-dashed border-border">
      <div className="text-xs font-medium text-foreground pt-1.5">
        Decoration
      </div>
      {betterments.map((b) => (
        <div
          key={b.id}
          className="flex items-start justify-between gap-3 text-xs"
        >
          <div className="min-w-0">
            <div className="text-foreground">{b.name}</div>
            <div className="text-muted-foreground">
              {b.supplierName} · qty {b.quantity}
            </div>
          </div>
          <div className="text-right shrink-0 text-muted-foreground">
            <div>Sell {formatCurrencyGbp(b.sellValue, 2)}</div>
            <div>Buy {formatCurrencyGbp(b.costValue, 2)}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// Quotes get a richer, PDF-like per-product layout: image, article number,
// description, supplier, and a price table showing BOTH sell and buy unit
// prices — a single flat cost figure next to a tiered sell table doesn't let
// anyone judge margin at a given order size. No computed margin column, just
// the raw figures, by explicit instruction.
//
// Columns come from quotePriceColumns() rather than the ladder directly:
// Promo Office publishes no tiers for most products, and a table driven
// straight off an empty ladder renders as row labels with nothing beside
// them. See packages/shared-core/src/domain/promo-office-pricing.ts.
// The five figures, defined once: a one-column product renders them as a
// compact list and a tiered one as a table, and neither should be able to
// drift from the other on what "Sell subtotal" means.
//
// `rule` draws the line above a row, `strong` is the emphasis the two
// subtotals carry.
function quotePriceRows(quotedOnly: boolean) {
  return [
    {
      label: quotedOnly ? "Quantity quoted" : "Quantity",
      value: (column: QuotePriceColumn) => String(column.quantity),
      rule: false,
      strong: false,
    },
    {
      label: "Sell price",
      value: (column: QuotePriceColumn) =>
        formatCurrencyGbp(column.sellUnitPrice, 2),
      rule: false,
      strong: false,
    },
    {
      label: "Sell subtotal",
      value: (column: QuotePriceColumn) =>
        formatCurrencyGbp(column.sellUnitPrice * column.quantity, 2),
      rule: true,
      strong: true,
    },
    {
      label: "Buy price",
      value: (column: QuotePriceColumn) =>
        formatCurrencyGbp(column.costUnitPrice, 2),
      rule: true,
      strong: false,
    },
    {
      label: "Buy subtotal",
      value: (column: QuotePriceColumn) =>
        formatCurrencyGbp(column.costUnitPrice * column.quantity, 2),
      rule: false,
      strong: true,
    },
  ];
}

function QuoteLineItemCard({ line }: { line: PromoOfficeLineItem }) {
  const columns = quotePriceColumns(line);
  const quotedOnly = !hasPublishedLadder(line);
  const rows = quotePriceRows(quotedOnly);
  return (
    <div className="flex gap-4 py-4 border-b border-border last:border-0">
      {/* A charge (carriage, origination, tree planting) has no product to
          picture, so it gets no image box; the spacer keeps its text in line
          with the products above and below it. */}
      {line.lineKind === "product" ? (
        <LineItemThumbnail
          imageUrl={line.imageUrl}
          description={line.description}
        />
      ) : (
        <div className="size-20 shrink-0" aria-hidden />
      )}
      <div className="flex-1 space-y-2 min-w-0">
        <div>
          <div className="text-xs text-muted-foreground font-mono">
            {line.articleNumber ?? "—"}
          </div>
          <div className="font-medium text-sm">{line.description}</div>
          <div className="text-xs text-muted-foreground">
            {line.supplierName ?? "—"}
          </div>
        </div>
        {/* One quantity is the common case, not the edge case: Promo Office
            publishes no tiers for most products. A full-width table for it
            stranded each label hard left and its single figure hard right,
            with a screen's width of nothing between them, so that case gets
            a narrow label/value list instead and the table is kept for
            products that genuinely have tiers to compare. */}
        {columns.length === 1 ? (
          <dl className="text-xs max-w-xs">
            {rows.map((row) => (
              <div
                key={row.label}
                className={cn(
                  "flex items-baseline justify-between gap-6 py-1",
                  row.rule && "border-t border-border",
                )}
              >
                <dt className="font-medium text-foreground">{row.label}</dt>
                <dd
                  className={cn(
                    "tabular-nums",
                    row.strong ? "font-medium" : "text-muted-foreground",
                  )}
                >
                  {row.value(columns[0])}
                </dd>
              </div>
            ))}
          </dl>
        ) : (
          <div className="overflow-x-auto">
            <table className="text-xs w-full">
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.label}
                    className={cn(
                      row.rule && "border-t border-border",
                      row.strong ? "font-medium" : "text-muted-foreground",
                    )}
                  >
                    <td className="pr-4 py-1 font-medium text-foreground">
                      {row.label}
                    </td>
                    {columns.map((column) => (
                      <td
                        key={column.quantity}
                        className="text-right px-2 py-1 tabular-nums"
                      >
                        {row.value(column)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {quotedOnly && (
          <p className="text-[11px] text-muted-foreground mt-1">
            Promo Office holds no quantity price breaks for this product —
            showing the price as quoted.
          </p>
        )}
        <LineBetterments betterments={line.betterments} />
      </div>
    </div>
  );
}

function HeaderFieldsSummary({
  customerReference,
  deliveryAddress,
  accountManagerName,
}: {
  customerReference: string | null;
  deliveryAddress: string | null;
  /** Quotes only -- Promo Office carries no account manager on an order.
      Stated as a plain fact about the quote: it used to appear only inside a
      sentence promising to email this person, which nothing ever did. */
  accountManagerName?: string | null;
}) {
  return (
    <dl className="grid gap-3 sm:grid-cols-2 text-sm max-w-xl">
      <div>
        <dt className="text-xs text-muted-foreground">Customer reference</dt>
        <dd>{customerReference ?? "—"}</dd>
      </div>
      <div>
        <dt className="text-xs text-muted-foreground">Delivery address</dt>
        <dd>{deliveryAddress ?? "—"}</dd>
      </div>
      {accountManagerName && (
        <div>
          <dt className="text-xs text-muted-foreground">
            Promo Office account manager
          </dt>
          <dd>{accountManagerName}</dd>
        </div>
      )}
    </dl>
  );
}

function PendingActionNotice({
  pendingAction,
}: {
  pendingAction: PromoOfficeTenderPendingAction;
}) {
  if (pendingAction.type === "accept") {
    return (
      <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
        <Clock className="size-4 shrink-0 mt-0.5" />
        <span>
          Accept requested {formatTimeAgo(pendingAction.requestedAt)} — the
          merchandise team has been notified to action this in Promo Office.
          Pending next sync.
        </span>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-2 text-sm text-muted-foreground bg-muted/50 border rounded-md px-3 py-2">
      <XCircle className="size-4 shrink-0 mt-0.5" />
      <span>
        Decline requested {formatTimeAgo(pendingAction.requestedAt)} — the
        merchandise team has been notified.
        {pendingAction.reason && <> Reason: "{pendingAction.reason}"</>}
      </span>
    </div>
  );
}

type TenderPanelMode = "view" | "declining";

function TenderDetailPanel({
  tender,
  onUpdate,
}: {
  tender: PromoOfficeTender;
  onUpdate: (updated: PromoOfficeTender) => void;
}) {
  const dataAdapter = useDataAdapter();
  const [mode, setMode] = useState<TenderPanelMode>("view");
  const [busy, setBusy] = useState(false);
  const [declineReason, setDeclineReason] = useState("");

  const handleAccept = async () => {
    setBusy(true);
    try {
      const updated = await dataAdapter.requestAcceptPromoOfficeTender(
        tender.id,
      );
      onUpdate(updated);
      toast("Merchandise team notified — awaiting Promo Office sync");
    } catch (err) {
      toast.error(mutationErrorMessage(err, "Could not accept this quote"));
    } finally {
      setBusy(false);
    }
  };

  const handleDeclineConfirm = async () => {
    if (!declineReason.trim()) return;
    setBusy(true);
    try {
      const updated = await dataAdapter.requestDeclinePromoOfficeTender(
        tender.id,
        declineReason.trim(),
      );
      onUpdate(updated);
      setMode("view");
      toast("Merchandise team notified of decline");
    } catch (err) {
      toast.error(mutationErrorMessage(err, "Could not decline this quote"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <HeaderFieldsSummary
          customerReference={tender.customerReference}
          deliveryAddress={tender.deliveryAddress}
          accountManagerName={tender.accountManagerName}
        />
        <Link
          to={paths.promoOfficeQuotePrint({ tenderId: tender.id })}
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0"
        >
          <Button size="sm" variant="outline">
            <Printer className="size-4 mr-1.5" />
            Generate customer quote
          </Button>
        </Link>
      </div>

      <div>
        {tender.lines.map((line) => (
          <QuoteLineItemCard key={line.id} line={line} />
        ))}
      </div>

      <div>
        {tender.pendingAction ? (
          <PendingActionNotice pendingAction={tender.pendingAction} />
        ) : mode === "declining" ? (
          <div className="space-y-2 max-w-xl">
            <Label htmlFor={`decline-reason-${tender.id}`}>
              Reason for declining
            </Label>
            <Textarea
              id={`decline-reason-${tender.id}`}
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              rows={2}
              placeholder="e.g. price too high, customer changed mind"
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="destructive"
                onClick={handleDeclineConfirm}
                disabled={busy || !declineReason.trim()}
              >
                Confirm decline
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setMode("view")}
                disabled={busy}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex gap-2">
              <Button size="sm" onClick={handleAccept} disabled={busy}>
                Accept &amp; convert to order
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-red-600 hover:text-red-700"
                onClick={() => setMode("declining")}
                disabled={busy}
              >
                Decline
              </Button>
            </div>
            {/* Says what the request actually does. Neither button writes
                  to Promo Office: the request is recorded against this quote
                  and raised as a ticket for the merchandise team, who make
                  the real change in Promo Office themselves. An earlier
                  version of this paragraph promised an email to the named
                  account manager, which nothing sent and nothing could --
                  the vendor's API carries no account-manager email, so the
                  copy named a person it had already admitted it could not
                  reach. Every other message on this page (the pending
                  banners, both toasts) already said "merchandise team". */}
            <p className="text-xs text-muted-foreground max-w-md">
              Neither action changes anything in Promo Office. Your request is
              recorded against this quote and raised as a ticket for the
              merchandise team, who action it in Promo Office themselves — the
              quote updates here once the next sync picks up their change.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function TenderRow({
  tender,
  onUpdate,
  expanded,
  onToggle,
}: {
  tender: PromoOfficeTender;
  onUpdate: (updated: PromoOfficeTender) => void;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <TableRow className="cursor-pointer" onClick={onToggle}>
        <TableCell className="w-6">
          {expanded ? (
            <ChevronDown className="h-3 w-3 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-3 w-3 text-muted-foreground" />
          )}
        </TableCell>
        <TableCell className="font-mono text-sm">
          <div className="flex flex-col items-start gap-1">
            {tender.tenderNumber}
            {/* A quote priced for a CIS draft estimate says so where the quote
                is listed, so nobody attaches it a second time from the lane. */}
            <LinkedDraftEstimateMarker link={tender.linkedDraftEstimate} />
          </div>
        </TableCell>
        <TableCell className="font-medium">
          {tender.customerName ?? "—"}
        </TableCell>
        <TableCell>
          <CisCustomerCell link={tender.customerLink} />
        </TableCell>
        <TableCell className="text-sm text-muted-foreground">
          {tender.projectName ?? "—"}
        </TableCell>
        <TableCell className="text-sm text-muted-foreground">
          {tender.lines.length} {tender.lines.length === 1 ? "line" : "lines"}
        </TableCell>
        <TableCell className="text-sm text-muted-foreground">
          {formatDate(tender.createdAt)}
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={7} className="bg-muted/30 p-0">
            <div
              // `whitespace-normal` undoes the TableCell primitive's
              // `whitespace-nowrap`, which is right for a one-line data cell
              // and wrong for a panel of prose: it left the footnote below
              // the accept/decline buttons running off the right of the
              // screen, because `max-w-md` sized the box but nowrap stopped
              // the text wrapping inside it.
              className="p-3 whitespace-normal"
              // Prevent the row's own expand/collapse toggle from firing
              // when interacting with form fields or buttons inside.
              onClick={(e) => e.stopPropagation()}
            >
              <TenderDetailPanel tender={tender} onUpdate={onUpdate} />
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

// Matches the backend's own default, so the page shows what it always showed
// -- the difference is that the other pages are now reachable.
const PAGE_SIZE = 25;

// Both tabs load the same way and fail the same way. Without this the
// rejected fetch left `rows === null` forever, so a failed list read was
// indistinguishable from a slow one -- a permanent spinner.
function LoadFailedCard({
  noun,
  onRetry,
}: {
  noun: string;
  onRetry: () => void;
}) {
  return (
    <Card className="p-12 text-center space-y-3">
      <AlertTriangle className="w-8 h-8 mx-auto text-amber-600" />
      <p className="text-sm text-muted-foreground">
        The {noun} couldn&apos;t be loaded.
      </p>
      <Button variant="outline" size="sm" onClick={onRetry}>
        Try again
      </Button>
    </Card>
  );
}

function TendersTab({ neverRefreshed }: { neverRefreshed: boolean }) {
  const dataAdapter = useDataAdapter();
  const [tenders, setTenders] = useState<PromoOfficeTender[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [retryTick, setRetryTick] = useState(0);
  // One row open at a time: the open row is the tab's state, not each row's.
  // Held as an id rather than an index so it survives a list that reorders.
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setTenders(null);
    setLoadFailed(false);
    dataAdapter
      .listPromoOfficeTenders({ page, pageSize: PAGE_SIZE })
      .then((response) => {
        if (cancelled) return;
        setTenders(response.items);
        setTotalPages(response.totalPages);
      })
      .catch(() => {
        if (cancelled) return;
        setLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, page, retryTick]);

  const handleUpdate = (updated: PromoOfficeTender) => {
    setTenders(
      (current) =>
        current?.map((t) => (t.id === updated.id ? updated : t)) ?? current,
    );
  };

  const handlePageChange = (next: number) => {
    setExpandedId(null);
    setPage(next);
  };

  if (loadFailed) {
    return (
      <LoadFailedCard
        noun="open quotes"
        onRetry={() => setRetryTick((n) => n + 1)}
      />
    );
  }

  if (tenders === null) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (tenders.length === 0) {
    return (
      <Card className="p-12 text-center">
        <Gift className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
        <h3 className="mb-1">
          {neverRefreshed ? "Nothing cached yet" : "No open quotes"}
        </h3>
        <p className="text-muted-foreground text-sm">
          {/* An empty list is not evidence about Promo Office until something
              has actually asked it. Before the first refresh the honest
              statement is about CIS, not about the vendor -- claiming
              "nothing currently open" on a cache that has never been
              populated is the same false-confidence seam as a partial sweep
              reporting itself OK. */}
          {neverRefreshed
            ? "The first refresh has not run yet, so nothing has been fetched from Promo Office."
            : page > 1
              ? "This page is empty — quotes may have closed since it was last loaded."
              : "Nothing currently open in Promo Office."}
        </p>
        {page > 1 && (
          <Button
            size="sm"
            variant="outline"
            className="mt-4"
            onClick={() => handlePageChange(1)}
          >
            Back to the first page
          </Button>
        )}
      </Card>
    );
  }

  return (
    <div className="rounded-lg border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-6" />
            <TableHead>Tender</TableHead>
            <TableHead>Customer</TableHead>
            <TableHead>Customer in One</TableHead>
            <TableHead>Project</TableHead>
            <TableHead>Lines</TableHead>
            <TableHead>Created</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {tenders.map((tender) => (
            <TenderRow
              key={tender.id}
              tender={tender}
              onUpdate={handleUpdate}
              expanded={expandedId === tender.id}
              onToggle={() =>
                setExpandedId((current) =>
                  current === tender.id ? null : tender.id,
                )
              }
            />
          ))}
        </TableBody>
      </Table>
      <ListPagination
        page={page}
        totalPages={totalPages}
        onPageChange={handlePageChange}
      />
    </div>
  );
}

function IndentRow({
  indent,
  expanded,
  onToggle,
}: {
  indent: PromoOfficeIndent;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <TableRow className="cursor-pointer" onClick={onToggle}>
        <TableCell className="w-6">
          {expanded ? (
            <ChevronDown className="h-3 w-3 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-3 w-3 text-muted-foreground" />
          )}
        </TableCell>
        <TableCell className="font-mono text-sm">
          {indent.indentNumber}
        </TableCell>
        <TableCell className="font-medium">
          {indent.customerName ?? "—"}
        </TableCell>
        <TableCell>
          <CisCustomerCell link={indent.customerLink} />
        </TableCell>
        <TableCell className="text-sm text-muted-foreground">
          {indent.projectName ?? "—"}
        </TableCell>
        <TableCell className="text-sm text-muted-foreground">
          {indent.lines.length} {indent.lines.length === 1 ? "line" : "lines"}
        </TableCell>
        <TableCell className="text-right">
          {formatCurrencyGbp(indent.totalValue, 2)}
        </TableCell>
        <TableCell className="text-sm text-muted-foreground">
          {formatDate(indent.orderedAt)}
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={8} className="bg-muted/30 p-0">
            <div className="p-3 space-y-4 whitespace-normal">
              <HeaderFieldsSummary
                customerReference={indent.customerReference}
                deliveryAddress={indent.deliveryAddress}
              />
              <OrderLineItemsTable lines={indent.lines} />
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function IndentsTab({ neverRefreshed }: { neverRefreshed: boolean }) {
  const dataAdapter = useDataAdapter();
  const [indents, setIndents] = useState<PromoOfficeIndent[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [retryTick, setRetryTick] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setIndents(null);
    setLoadFailed(false);
    dataAdapter
      .listPromoOfficeIndents({ page, pageSize: PAGE_SIZE })
      .then((response) => {
        if (cancelled) return;
        setIndents(response.items);
        setTotalPages(response.totalPages);
      })
      .catch(() => {
        if (cancelled) return;
        setLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, page, retryTick]);

  const handlePageChange = (next: number) => {
    setExpandedId(null);
    setPage(next);
  };

  if (loadFailed) {
    return (
      <LoadFailedCard
        noun="open orders"
        onRetry={() => setRetryTick((n) => n + 1)}
      />
    );
  }

  if (indents === null) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (indents.length === 0) {
    return (
      <Card className="p-12 text-center">
        <Gift className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
        <h3 className="mb-1">
          {neverRefreshed ? "Nothing cached yet" : "No open orders"}
        </h3>
        <p className="text-muted-foreground text-sm">
          {neverRefreshed
            ? "The first refresh has not run yet, so nothing has been fetched from Promo Office."
            : page > 1
              ? "This page is empty — orders may have been invoiced since it was last loaded."
              : "Nothing currently in process and awaiting invoice in Promo Office."}
        </p>
        {page > 1 && (
          <Button
            size="sm"
            variant="outline"
            className="mt-4"
            onClick={() => handlePageChange(1)}
          >
            Back to the first page
          </Button>
        )}
      </Card>
    );
  }

  return (
    <div className="rounded-lg border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-6" />
            <TableHead>Indent</TableHead>
            <TableHead>Customer</TableHead>
            <TableHead>Customer in One</TableHead>
            <TableHead>Project</TableHead>
            <TableHead>Lines</TableHead>
            <TableHead className="text-right">Value</TableHead>
            <TableHead>Ordered</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {indents.map((indent) => (
            <IndentRow
              key={indent.id}
              indent={indent}
              expanded={expandedId === indent.id}
              onToggle={() =>
                setExpandedId((current) =>
                  current === indent.id ? null : indent.id,
                )
              }
            />
          ))}
        </TableBody>
      </Table>
      <ListPagination
        page={page}
        totalPages={totalPages}
        onPageChange={handlePageChange}
      />
    </div>
  );
}

export function PromoOfficePage() {
  const merchandiseEnabled = isFeatureEnabled("merchandise_draft_estimates");
  // Supplier linking sits behind the Promo Office channel (merchandise spec 11
  // §9): its routes 404 while the flag is off, so the tab and its count stay
  // off the page rather than reporting a failed read.
  const supplierLinkingEnabled = isFeatureEnabled("promo_office_channel");
  const dataAdapter = useDataAdapter();
  const [syncStatus, setSyncStatus] = useState<PromoOfficeSyncStatus | null>(
    null,
  );
  const [tenderCount, setTenderCount] = useState<number | null>(null);
  const [indentCount, setIndentCount] = useState<number | null>(null);
  const [unlinkedCount, setUnlinkedCount] = useState<number | null>(null);
  const [unlinkedQuoteCount, setUnlinkedQuoteCount] = useState<number | null>(
    null,
  );
  const [unlinkedSupplierCount, setUnlinkedSupplierCount] = useState<
    number | null
  >(null);
  // Bumped when a customer is linked, so the counts and both quote lists
  // re-read. A resolved customer changes three surfaces at once: it leaves
  // this list and its quotes and orders stop saying "No customer in One".
  const [resolvedTick, setResolvedTick] = useState(0);

  // The staleness indicator and the two tab counts are decorative: each one
  // renders nothing at all when it has no value, and the tabs below report
  // their own load failures. Swallowing is therefore the right behaviour --
  // but it has to be explicit, or a failed read surfaces as an unhandled
  // rejection instead of as a missing badge.
  useEffect(() => {
    dataAdapter
      .getPromoOfficeSyncStatus()
      .then(setSyncStatus)
      .catch(() => setSyncStatus(null));
    dataAdapter
      .listPromoOfficeTenders({ pageSize: 1 })
      .then((r) => setTenderCount(r.total))
      .catch(() => setTenderCount(null));
    dataAdapter
      .listPromoOfficeIndents({ pageSize: 1 })
      .then((r) => setIndentCount(r.total))
      .catch(() => setIndentCount(null));
    dataAdapter
      .listPromoOfficeUnlinkedCustomers({ pageSize: 1 })
      .then((r) => setUnlinkedCount(r.total))
      .catch(() => setUnlinkedCount(null));
    if (merchandiseEnabled) {
      dataAdapter
        .listPromoOfficeUnlinkedQuotes({ pageSize: 1 })
        .then((r) => setUnlinkedQuoteCount(r.total))
        .catch(() => setUnlinkedQuoteCount(null));
    }
    if (supplierLinkingEnabled) {
      dataAdapter
        .listPromoOfficeUnlinkedSuppliers({ pageSize: 1 })
        .then((r) => setUnlinkedSupplierCount(r.total))
        .catch(() => setUnlinkedSupplierCount(null));
    }
  }, [dataAdapter, resolvedTick, merchandiseEnabled, supplierLinkingEnabled]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1>Promo Office</h1>
          <p className="text-sm text-muted-foreground">
            Read-only visibility into Promo Office's currently-open quotes and
            orders.
          </p>
        </div>
        <StalenessIndicator status={syncStatus} />
      </div>

      <Tabs defaultValue="quotes">
        <TabsList variant="underline">
          <TabsTrigger value="quotes">
            Open Quotes
            {tenderCount !== null && (
              <TabsTriggerCount>{tenderCount}</TabsTriggerCount>
            )}
          </TabsTrigger>
          <TabsTrigger value="orders">
            Open Orders
            {indentCount !== null && (
              <TabsTriggerCount>{indentCount}</TabsTriggerCount>
            )}
          </TabsTrigger>
          {/* A state, not an instruction. "Needs linking" would imply somebody
              in particular has been asked; this is open to everyone who can
              see a quote, which is the whole reason it sits here rather than
              in a queue of its own. */}
          <TabsTrigger value="no-cis-customer">
            No customer in One
            {unlinkedCount !== null && (
              <TabsTriggerCount>{unlinkedCount}</TabsTriggerCount>
            )}
          </TabsTrigger>
          {/* The supplier twin, beside it (merchandise spec 11, Q7). Open to
              everyone who can see a quote; deciding needs orders:manage. */}
          {supplierLinkingEnabled && (
            <TabsTrigger value="no-cis-supplier">
              No supplier in One
              {unlinkedSupplierCount !== null && (
                <TabsTriggerCount>{unlinkedSupplierCount}</TabsTriggerCount>
              )}
            </TabsTrigger>
          )}
          {/* Open quotes with no recognised merchandise request number — the lane that
              catches a merchandise quote saved without its reference. */}
          {merchandiseEnabled && (
            <TabsTrigger value="unlinked-quotes">
              Unlinked quotes
              {unlinkedQuoteCount !== null && (
                <TabsTriggerCount>{unlinkedQuoteCount}</TabsTriggerCount>
              )}
            </TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="quotes" className="pt-4">
          <TendersTab
            key={`quotes-${resolvedTick}`}
            neverRefreshed={syncStatus?.refreshStatus === "never_run"}
          />
        </TabsContent>
        <TabsContent value="orders" className="pt-4">
          <IndentsTab
            key={`orders-${resolvedTick}`}
            neverRefreshed={syncStatus?.refreshStatus === "never_run"}
          />
        </TabsContent>
        <TabsContent value="no-cis-customer" className="pt-4">
          <UnlinkedCustomersTab
            onResolved={() => setResolvedTick((n) => n + 1)}
          />
        </TabsContent>
        {supplierLinkingEnabled && (
          <TabsContent value="no-cis-supplier" className="pt-4">
            <UnlinkedSuppliersTab
              onResolved={() => setResolvedTick((n) => n + 1)}
            />
          </TabsContent>
        )}
        {merchandiseEnabled && (
          <TabsContent value="unlinked-quotes" className="pt-4">
            <UnlinkedQuotesTab
              onResolved={() => setResolvedTick((n) => n + 1)}
            />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
